import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

import { getClassNames } from '../../../../utils/util';

import TextInput from '../text-input/TextInput';
import Checkbox from '../checkbox/Checkbox';

import { ReactComponent as ArrowSingle } from '../../../../resources/icons/arrow-single.svg';

import './dropdown.scss';

const Dropdown = ({
  name,
  options,
  value,
  onChange,
  disabled,
  placeholder,
  getOptionLabel,
  getOptionValue,
  error,
  multi,
  noSort,
}) => {
  const [showError, setShowError] = useState(error);
  const [filter, setFilter] = useState('');
  const [sortedOptions, setSortedOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [showOptions, setShowOptions] = useState(false);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const handleClickOutside = useCallback(
    (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOptions(false);
        setFilter('');
        setFilteredOptions(sortedOptions);
      }
    },
    [sortedOptions]
  );

  const sortOptions = useCallback(() => {
    if (!options) return [];

    if (noSort) return options;

    const copyOfOptions = [...options];

    copyOfOptions.sort((optionOne, optionTwo) => {
      let optionOneLabel = optionOne.label;
      let optionTwoLabel = optionTwo.label;

      if (getOptionLabel) {
        optionOneLabel = getOptionLabel(optionOne);
        optionTwoLabel = getOptionLabel(optionTwo);
      }

      optionOneLabel = optionOneLabel.toLowerCase().trim();
      optionTwoLabel = optionTwoLabel.toLowerCase().trim();

      if (optionOneLabel < optionTwoLabel) return -1;
      if (optionOneLabel > optionTwoLabel) return 1;
      return 0;
    });

    return copyOfOptions;
  }, [getOptionLabel, options, noSort]);

  useEffect(() => {
    setShowError(error);
  }, [error]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => {
    const sortedOptions = sortOptions();

    setSortedOptions(sortedOptions);
    setFilteredOptions(sortedOptions);
  }, [sortOptions]);

  const filterOptions = (value) => {
    if (value === '') {
      setFilteredOptions(sortedOptions);
      return;
    }

    const filteredOptions = sortedOptions.filter((option) => {
      let label = option.label;

      if (getOptionLabel) {
        label = getOptionLabel(option);
      }

      return label.toLowerCase().includes(value.toLowerCase());
    });

    setFilteredOptions(filteredOptions);
  };

  const getPlaceholder = () => {
    if (!value || value.length === 0) return placeholder;

    if (multi) {
      const values = value.map((v) => v.label.toString().trim());
      return values.join(', ');
    } else {
      let label = value.label;

      if (getOptionLabel) {
        label = getOptionLabel(value);
      }

      if (label) {
        return label.toString().trim();
      } else {
        return placeholder;
      }
    }
  };

  const handleTextInputChange = (event) => {
    const { value } = event.target;

    filterOptions(value);
    setFilter(value);
  };

  const handleIconMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (showOptions) {
      setShowOptions(false);
      setFilter('');
      setFilteredOptions(sortedOptions);
      inputRef.current.blur();
    } else {
      inputRef.current.focus();
    }
  };

  const handleTextInputFocus = () => {
    setShowOptions(true);
  };

  const handleOptionMouseDown = (option) => {
    setShowError(false);

    if (multi) {
      const index = value.findIndex((value) => {
        return value.value === option.value;
      });

      if (index === -1) {
        onChange([...value, option]);
      } else {
        const newValue = [...value];
        newValue.splice(index, 1);
        onChange(newValue);
      }
    } else {
      onChange(option);
      setShowOptions(false);
      setFilter('');
      setFilteredOptions(options);
    }
  };

  const renderOptions = () => {
    if (filteredOptions.length === 0) {
      return <li className="dropdown__option dropdown__option--empty">No Options</li>;
    }

    return filteredOptions.map((option) => {
      let optionLabel = option.label;
      let optionValue = option.value;

      if (getOptionLabel) {
        optionLabel = getOptionLabel(option);
      }

      if (getOptionValue) {
        optionValue = getOptionValue(option);
      }

      let selected;
      if (multi) {
        selected = value.some((v) => {
          let selectedOptionValue;

          if (v) {
            selectedOptionValue = v.value;

            if (getOptionValue) {
              selectedOptionValue = getOptionValue(v);
            }
          }

          return selectedOptionValue === optionValue;
        });
      } else {
        let selectedOptionValue;

        if (value) {
          selectedOptionValue = value.value;

          if (getOptionValue) {
            selectedOptionValue = getOptionValue(value);
          }
        }

        selected = optionValue === selectedOptionValue;
      }

      return (
        <li
          className={getClassNames('dropdown__option', { selected, multi })}
          key={optionValue}
          onMouseDown={() => handleOptionMouseDown(option)}
        >
          {multi && (
            <Checkbox
              value={selected}
              onChange={(event) => {
                handleOptionMouseDown(option);
                event.stopPropagation();
              }}
            />
          )}
          <p className="dropdown__option__text">{optionLabel}</p>
        </li>
      );
    });
  };

  return (
    <div className="dropdown" ref={dropdownRef}>
      <TextInput
        ref={inputRef}
        name={name}
        value={filter}
        onChange={handleTextInputChange}
        placeholder={getPlaceholder()}
        onFocus={handleTextInputFocus}
        error={showError}
        disabled={disabled}
        icon={
          <ArrowSingle
            className={getClassNames('dropdown__icon', { open: showOptions })}
            onMouseDown={handleIconMouseDown}
          />
        }
        darkPlaceholder
        autoComplete="off"
      />

      {showOptions && <ul className="dropdown__options">{renderOptions()}</ul>}
    </div>
  );
};

Dropdown.propTypes = {
  name: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  value: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.arrayOf(PropTypes.shape({}))]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
  error: PropTypes.bool,
  multi: PropTypes.bool,
  noSort: PropTypes.bool,
};

Dropdown.defaultProps = {
  name: undefined,
  value: undefined,
  disabled: false,
  placeholder: 'Select...',
  getOptionLabel: undefined,
  getOptionValue: undefined,
  error: false,
  multi: false,
  noSort: false,
};

export default Dropdown;
